/**
 * Product Gallery Zoom Component
 * Fullscreen zoom viewer on image tap; pinch + double-tap zoom on mobile
 */
class ProductGallery extends HTMLElement {
  constructor() {
    super();

    this.mobileMatchMedia = window.matchMedia('(max-width: 1024px)');
    this.galleryViewerElement = this.querySelector('.product-gallery-viewer');
    this.galleryViewerElementImage = this.galleryViewerElement.querySelector('.product-gallery-viewer-image');
    this.galleryViewerImage = this.galleryViewerElement.querySelector('[data-viewer-image]');
    this.galleryClose = this.querySelector('[data-close]');

    this.productGalleryThumbnailsHeight = ((32 + 53 + 32) / 2) * -1;

    this.thumbnails = this.querySelectorAll('.product-gallery-thumbnails button');
    this.thumbnailMain = this.querySelector('.product-gallery-thumbnails');
    this.activeThumbnail = this.thumbnails[0];

    this.isDragging = false;
    this.imageViewerOpen = false;
    this.isViewing360 = false;
    this.galleryActiveImage = null;
    this.currentScroll = 0;

    // Gallery open: tap vs swipe
    this.pointerDown = null;
    this.tapThreshold = 12;

    // Pinch / double-tap zoom inside viewer
    this.zoomScale = 1;
    this.minZoom = 1;
    this.maxZoom = 4;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.lastTapTime = 0;
    this.didPinch = false;
    this.didDrag = false;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onDocumentPointerUp = this.onDocumentPointerUp.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
    this.onViewerTouchStart = this.onViewerTouchStart.bind(this);
    this.onViewerTouchMove = this.onViewerTouchMove.bind(this);
    this.onViewerTouchEnd = this.onViewerTouchEnd.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onClick = this.onClick.bind(this);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });

    if (this.galleryClose) {
      this.galleryClose.addEventListener('click', this.closeGallery.bind(this));
    }

    document.addEventListener('keydown', this.onDocumentKeyDown);

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);

    if (this.galleryViewerImage) {
      this.galleryViewerImage.addEventListener('mousedown', this.onMouseDown);
      this.galleryViewerImage.addEventListener('click', this.onClick);
      this.galleryViewerImage.addEventListener('touchstart', this.onViewerTouchStart, { passive: false });
      this.galleryViewerImage.addEventListener('touchmove', this.onViewerTouchMove, { passive: false });
      this.galleryViewerImage.addEventListener('touchend', this.onViewerTouchEnd);
      this.galleryViewerImage.addEventListener('touchcancel', this.onViewerTouchEnd);
    }

    window.addEventListener('resize', this.debounce(this.onResize.bind(this), 150));

    // Tap-to-open on product gallery images (works with Swiper)
    document.addEventListener('pointerdown', this.onDocumentPointerDown, { passive: true });
    document.addEventListener('pointerup', this.onDocumentPointerUp, { passive: true });
  }

  onDocumentKeyDown(e) {
    if (e.key === 'Escape' && this.imageViewerOpen) {
      this.closeGallery();
    }
  }

  getMediaIdFromElement(el) {
    if (!el) return null;
    if (el.dataset?.mediaId && /^\d+$/.test(el.dataset.mediaId)) {
      return el.dataset.mediaId;
    }
    const withId = el.closest?.('[data-media-id]');
    if (!withId) return null;
    const raw = withId.dataset.mediaId;
    if (/^\d+$/.test(raw)) return raw;
    const match = raw.match(/(\d+)$/);
    return match ? match[1] : raw.split('-').pop();
  }

  isProductGalleryImage(target) {
    if (!(target instanceof Element)) return false;
    if (target.closest('.shop-the-look-pill, .shop-the-look-pill-opener, button, a')) return false;
    const img = target.closest('.product__media-wrapper .product__media img, .product__media-wrapper .product-media-container img');
    return img || null;
  }

  onDocumentPointerDown(event) {
    if (this.imageViewerOpen) return;
    const img = this.isProductGalleryImage(event.target);
    if (!img) return;
    this.pointerDown = {
      x: event.clientX,
      y: event.clientY,
      img,
      pointerId: event.pointerId,
    };
  }

  onDocumentPointerUp(event) {
    if (!this.pointerDown || this.imageViewerOpen) {
      this.pointerDown = null;
      return;
    }
    if (event.pointerId !== this.pointerDown.pointerId) return;

    const dx = Math.abs(event.clientX - this.pointerDown.x);
    const dy = Math.abs(event.clientY - this.pointerDown.y);
    const img = this.pointerDown.img;
    this.pointerDown = null;

    // Ignore swipes / drags (Swiper carousel)
    if (dx > this.tapThreshold || dy > this.tapThreshold) return;

    const mediaId = this.getMediaIdFromElement(img);
    if (mediaId) {
      this.openGalleryWithMediaId(mediaId, img);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  onResize() {
    if (this.imageViewerOpen) {
      requestAnimationFrame(() => {
        this.setInitialPosition();
        this.applyZoomTransform();
      });
    }
  }

  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  applyZoomTransform() {
    if (!this.galleryViewerImage) return;
    this.galleryViewerImage.style.transform = `scale(${this.zoomScale})`;
    this.galleryViewerImage.style.transformOrigin = 'center center';
    this.galleryViewerImage.classList.toggle('is-zoomed', this.zoomScale > 1.01);
  }

  resetZoom() {
    this.zoomScale = 1;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.didPinch = false;
    if (this.galleryViewerImage) {
      this.galleryViewerImage.style.transform = '';
      this.galleryViewerImage.classList.remove('is-zoomed');
    }
  }

  onViewerTouchStart(event) {
    if (!this.imageViewerOpen || !this.mobileMatchMedia.matches || this.isViewing360) return;

    if (event.touches.length === 2) {
      this.didPinch = true;
      this.isDragging = false;
      this.pinchStartDistance = this.getTouchDistance(event.touches);
      this.pinchStartScale = this.zoomScale;
      event.preventDefault();
      return;
    }

    if (event.touches.length === 1) {
      this.didDrag = false;
      this.isDragging = true;
      this.startX = event.touches[0].clientX;
      this.startY = event.touches[0].clientY;
      this.initialX = this.galleryViewerImage.offsetLeft;
      this.initialY = this.galleryViewerImage.offsetTop;
    }
  }

  onViewerTouchMove(event) {
    if (!this.imageViewerOpen || !this.mobileMatchMedia.matches) return;

    if (event.touches.length === 2 && this.pinchStartDistance > 0) {
      event.preventDefault();
      this.didPinch = true;
      this.isDragging = false;
      const distance = this.getTouchDistance(event.touches);
      const nextScale = (distance / this.pinchStartDistance) * this.pinchStartScale;
      this.zoomScale = Math.min(this.maxZoom, Math.max(this.minZoom, nextScale));
      this.applyZoomTransform();
      return;
    }

    if (this.isDragging && this.galleryActiveImage && event.touches.length === 1) {
      const touch = event.touches[0];
      const dx = touch.clientX - this.startX;
      const dy = touch.clientY - this.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.didDrag = true;
      }

      if (this.zoomScale > 1.01) {
        event.preventDefault();
        this.galleryViewerImage.style.left = `${this.initialX + dx}px`;
        this.galleryViewerImage.style.top = `${this.initialY + dy}px`;
        return;
      }

      const newLeft = this.initialX + dx;
      const newTop = this.initialY + dy;

      if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
        this.galleryViewerImage.style.left = `${this.initialX + dx}px`;
      }
      if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
        this.galleryViewerImage.style.top = `${this.initialY + dy}px`;
      }
    }
  }

  onViewerTouchEnd(event) {
    if (!this.imageViewerOpen || !this.mobileMatchMedia.matches) {
      this.isDragging = false;
      return;
    }

    if (event.touches.length < 2) {
      this.pinchStartDistance = 0;
    }

    if (event.touches.length === 0) {
      this.isDragging = false;

      // Double-tap to toggle zoom (ignore after pinch/drag)
      if (!this.didPinch && !this.didDrag && event.changedTouches.length === 1) {
        const now = Date.now();
        if (now - this.lastTapTime < 300) {
          this.zoomScale = this.zoomScale > 1.5 ? 1 : 2.5;
          this.applyZoomTransform();
          if (this.zoomScale === 1) {
            this.setInitialPosition();
          }
          this.lastTapTime = 0;
        } else {
          this.lastTapTime = now;
        }
      }

      this.didPinch = false;
      this.didDrag = false;
    }
  }

  onMouseMove(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    if (this.isDragging && this.galleryActiveImage) {
      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      const newLeft = this.initialX + dx;
      const newTop = this.initialY + dy;

      if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
        this.galleryViewerImage.style.left = this.initialX + dx + 'px';
      }

      if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
        this.galleryViewerImage.style.top = this.initialY + dy + 'px';
      }
    }
  }

  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      if (this.galleryViewerImage) {
        this.galleryViewerImage.style.cursor = 'grab';
      }
    }
  }

  onMouseDown(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.initialX = this.galleryViewerImage.offsetLeft;
    this.initialY = this.galleryViewerImage.offsetTop;
    this.galleryViewerImage.style.cursor = 'grabbing';
  }

  onClick() {
    if (!this.mobileMatchMedia.matches) {
      this.closeGallery();
    }
  }

  setInitialPosition() {
    if (!this.imageViewerOpen || !this.galleryActiveImage) {
      return;
    }

    if (!this.mobileMatchMedia.matches) {
      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;
      this.galleryViewerImage.style.left = '0px';
    } else {
      const topPosition =
        (this.galleryActiveImage.offsetHeight - window.innerHeight) / 2 - this.productGalleryThumbnailsHeight;
      const leftPosition = (this.galleryActiveImage.offsetWidth - window.innerWidth) / 2;
      this.galleryViewerImage.style.top = `${-topPosition}px`;
      this.galleryViewerImage.style.left = `${-leftPosition}px`;
    }
  }

  handleThumbnailClick(event) {
    const thisTarget = event.currentTarget;
    const mediaId = thisTarget.dataset.mediaId;

    this.resetZoom();

    if (this.galleryActiveImage) {
      this.galleryActiveImage.classList.add('is-hidden');
    }

    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${mediaId}"]`);

    if (this.galleryActiveImage) {
      this.galleryActiveImage.classList.remove('is-hidden');
      this.setActiveThumbnail(mediaId);

      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;

      this.setInitialPosition();
    }
  }

  openGalleryWithMediaId(mediaId, triggerElement) {
    this.galleryTriggerImage = triggerElement;
    this.resetZoom();

    this.galleryViewerImage.classList.add('overflow-hidden');

    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${mediaId}"]`);

    if (!this.galleryActiveImage) {
      this.galleryActiveImage = this.galleryViewerImage.querySelector('img');
    }

    if (!this.galleryActiveImage) {
      console.warn('No gallery image found');
      return;
    }

    this.galleryViewerImage.querySelectorAll('img').forEach((image) => {
      image.setAttribute('loading', 'eager');
    });

    this.galleryViewerImage.querySelectorAll('img').forEach((img) => {
      if (String(img.dataset.mediaId) === String(mediaId)) {
        img.classList.remove('is-hidden');
      } else {
        img.classList.add('is-hidden');
      }
    });

    this.imageViewerOpen = true;
    this.galleryViewerElement.classList.remove('is-hidden');
    document.body.classList.add('gallery-zoom-open');

    setTimeout(() => {
      this.setInitialPosition();

      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;

      this.disableBodyScroll();

      setTimeout(() => {
        this.galleryViewerElement.classList.remove('is-slide-down');
        this.galleryViewerElement.classList.add('is-visible');

        this.setActiveThumbnail(mediaId);

        this.waitForTransition(this.galleryViewerElement).then(() => {
          const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
          this.galleryViewerImage.scrollTop = heightDifference / 2;
          this.galleryViewerImage.classList.remove('overflow-hidden');

          this.thumbnailMain.classList.remove('is-hidden');

          setTimeout(() => {
            this.thumbnailMain.classList.remove('is-transparent');
            this.galleryClose.classList.remove('is-transparent');
          }, 100);
        });
      }, 50);
    }, 50);
  }

  setActiveThumbnail(mediaId) {
    const newActiveThumbnail = this.querySelector(`.thumbnail-button[data-media-id="${mediaId}"]`);

    if (this.activeThumbnail) {
      this.activeThumbnail.classList.remove('is-active');
      this.activeThumbnail.setAttribute('aria-current', 'false');
    }

    if (newActiveThumbnail) {
      this.activeThumbnail = newActiveThumbnail;
      this.activeThumbnail.classList.add('is-active');
      this.activeThumbnail.setAttribute('aria-current', 'true');
    }
  }

  closeGallery() {
    this.thumbnailMain.classList.add('is-transparent');
    this.galleryClose.classList.add('is-transparent');
    this.imageViewerOpen = false;
    this.isViewing360 = false;
    this.resetZoom();

    this.waitForTransition(this.thumbnailMain).then(() => {
      this.thumbnailMain.classList.add('is-hidden');
      this.galleryViewerImage.classList.add('overflow-hidden');

      this.enableBodyScroll();
      document.body.classList.remove('gallery-zoom-open');

      setTimeout(() => {
        this.galleryViewerElement.classList.remove('is-visible');
        this.galleryViewerElement.classList.add('is-slide-down');

        this.waitForTransition(this.galleryViewerElement).then(() => {
          this.galleryViewerElement.classList.add('is-hidden');

          this.galleryViewerImage.querySelectorAll('img:not(.is-hidden)').forEach((image) => {
            image.classList.add('is-hidden');
          });

          setTimeout(() => {
            if (this.galleryTriggerImage) {
              this.galleryTriggerImage.focus();
            }
          }, 100);
        });
      }, 66);
    });
  }

  waitForTransition(element) {
    return new Promise((resolve) => {
      const handleTransitionEnd = (e) => {
        if (e.target === element) {
          element.removeEventListener('transitionend', handleTransitionEnd);
          resolve();
        }
      };

      element.addEventListener('transitionend', handleTransitionEnd);
      setTimeout(resolve, 600);
    });
  }

  disableBodyScroll() {
    this.currentScroll = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.currentScroll}px`;
    document.body.style.width = '100%';
  }

  enableBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.currentScroll);
  }
}

customElements.define('product-gallery', ProductGallery);
